// Operator-facing labels for `comms_log.type` (spec-16 dec-1). DISPLAY-ONLY: the raw
// `type` key stays the source of truth for grouping, filtering and link values — this
// map only changes what an operator READS. Mirrors the CHANNEL_LABEL pattern already
// used in CommsTimeline.tsx / CommsPage.tsx.
//
// The `activation.*` keys are canonical in memex-ai `services/email/templates.ts`,
// stamped as `commsType` by the drip builders: spec-427's cohort win-backs
// (`connected_inactive` / `signed_in_dormant`) and spec-453's activation touches
// (`verified_milestone` / `connect_people`). If memex-ai renames an email or adds a
// sequence touch, this Backstage-side map can drift from the emails' canonical names —
// commsTypeLabels.test.ts enumerates the known keys and fails when one has no label.

// The activation subtypes memex-ai's drip currently ships (spec-427 + spec-453). This
// is maintained by hand against `services/email/templates.ts` and is INDEPENDENT of the
// label map below on purpose: the drift-guard test asserts every key here has a label,
// so adding a shipped key to this list without a label (or vice-versa) fails CI.
export const KNOWN_ACTIVATION_TYPES = [
  "activation.verified_milestone",
  "activation.connect_people",
  "activation.connected_inactive",
  "activation.signed_in_dormant",
] as const;

// The spec-453 "See it verified" email. Its send is idempotent — the atomic gate fires
// it exactly once per user, on their first-ever AC reaching `verified` — so counting
// these sends counts distinct users who hit that milestone (spec-16 dec-2). This is the
// source of the "first verified-AC milestones" activation-health signal.
export const VERIFIED_MILESTONE_TYPE = "activation.verified_milestone";

// type → operator label. Kept separate from KNOWN_ACTIVATION_TYPES (see above).
const TYPE_LABEL: Record<string, string> = {
  "activation.verified_milestone": "See it verified",
  "activation.connect_people": "Connect with people",
  // spec-17: corrected to match memex-ai spec-487's reframing — connected_inactive is
  // the Day-2 "Connected but no Spec" nudge (not a win-back); signed_in_dormant is the
  // actual Day-3 win-back. Raw keys unchanged; this is display copy only.
  "activation.connected_inactive": "Connected, no Spec",
  "activation.signed_in_dormant": "Win-back (never connected)",
};

/**
 * Operator label for a `comms_log.type`. Unmapped types fall back to the raw key
 * (never blank) so a newly-shipped email type is legible-enough before its label lands.
 */
export function commsTypeLabel(type: string): string {
  return TYPE_LABEL[type] ?? type;
}
